/**
 * Enhanced Math Input Field with MathLive Integration
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Paper,
  Alert,
  Typography,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  Skeleton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Visibility,
  Code,
  Warning,
  CheckCircle,
  Keyboard,
  VisibilityOff,
} from '@mui/icons-material';
import type { MathfieldElement } from 'mathlive';
import { useMathLive } from '../hooks/useMathLive';
import LaTeXPreview from './LaTeXPreview';
import type { MathInputFieldProps } from '../types';
import { getFriendlyErrorMessage } from '../utils/latexHelpers';

const MathInputField: React.FC<MathInputFieldProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'Enter mathematical expression...',
  disabled = false,
  showPreview = true,
  onValidationChange,
}) => {
  const [mode, setMode] = useState<'visual' | 'source'>('visual');
  const [showValidation, setShowValidation] = useState(true);
  const [localValue, setLocalValue] = useState(value);
  const theme = useTheme();

  // MathLive integration
  const {
    mathfieldRef,
    isLoaded,
    error: mathLiveError,
    validation,
    isValidating,
    validateNow,
  } = useMathLive({
    value: localValue,
    onChange: (newValue) => {
      setLocalValue(newValue);
      onChange(newValue);
    },
    onValidationChange,
    disabled,
  });

  // Handle blur - validate and call parent onBlur
  const handleBlur = async () => {
    await validateNow();
    onBlur?.();
  };

  // Sync with parent value
  useEffect(() => {
    if (value !== localValue) {
      setLocalValue(value);
    }
  }, [value]); // Only depend on value, not localValue to avoid infinite loops

  // Sync mathfield with localValue when switching to visual mode
  useEffect(() => {
    if (mode === 'visual' && mathfieldRef.current && isLoaded) {
      const mathfield = mathfieldRef.current as MathfieldElement;
      if (mathfield.value !== localValue) {
        mathfield.value = localValue;
      }
    }
  }, [mode, localValue, isLoaded]);


  // Handle text field changes (source mode)
  const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setLocalValue(newValue);
    onChange(newValue);
  };

  const toggleMode = () => {
    setMode((prev) => {
      const newMode = prev === 'visual' ? 'source' : 'visual';
      // When switching from visual to source, ensure we have the latest value from mathfield
      if (newMode === 'source' && mathfieldRef.current && isLoaded) {
        const mathfield = mathfieldRef.current as MathfieldElement;
        const currentValue = mathfield.value;
        if (currentValue !== localValue) {
          setLocalValue(currentValue);
          onChange(currentValue);
        }
      }

      return newMode;
    });
  };

  const handleKeyboardToggle = () => {
    if (mathfieldRef.current && isLoaded) {
      const mathfield = mathfieldRef.current as MathfieldElement;
      mathfield.executeCommand('toggleVirtualKeyboard');
    }
  };

  // Render loading state
  if (!isLoaded && mode === 'visual') {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        {showPreview && <Skeleton variant="text" height={40} />}
      </Box>
    );
  }

  // Render error state for MathLive
  if (mathLiveError && mode === 'visual') {
    return (
      <Box>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {mathLiveError}. Falling back to text input.
        </Alert>
        <TextField
          fullWidth
          multiline
          minRows={2}
          maxRows={4}
          value={localValue}
          onChange={handleTextFieldChange}
          placeholder={placeholder}
          disabled={disabled}
          sx={{ fontFamily: 'monospace' }}
        />
        {showPreview && localValue && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Preview:
            </Typography>
            <LaTeXPreview latex={localValue} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box>

      {/* Mode Toggle and Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Tooltip title={mode === 'visual' ? 'Switch to LaTeX source code' : 'Switch to visual math editor'}>
          <IconButton size="small" onClick={toggleMode}>
            {mode === 'visual' ? <Code /> : <VisibilityOff />}
          </IconButton>
        </Tooltip>

        <Tooltip title="Toggle virtual keyboard">
          <IconButton
            size="small"
            onClick={handleKeyboardToggle}
            disabled={mode !== 'visual' || !isLoaded}
            sx={{ opacity: mode === 'visual' && isLoaded ? 1 : 0.5 }}
          >
            <Keyboard />
          </IconButton>
        </Tooltip>

        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showValidation}
              onChange={(e) => setShowValidation(e.target.checked)}
            />
          }
          label="Show validation"
          sx={{ ml: 'auto' }}
        />
      </Box>

      {/* Input Field */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          minHeight: 60,
          backgroundColor: disabled ? 'action.disabledBackground' : 'action.hover',
          '& math-field': {
            border: 'none',
            width: '100%',
            fontSize: '16px',
            padding: '8px',
            minHeight: '40px',
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderRadius: '4px',
            '&:focus': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: -2,
              backgroundColor: 'background.paper',
            },
          },
        }}
      >
        {mode === 'visual' && isLoaded ? (
          <math-field
            ref={mathfieldRef}
            onBlur={handleBlur}
            style={{
              border: `1px solid ${theme.palette.divider}`,
              width: '100%',
              fontSize: '16px',
              padding: '8px',
              minHeight: '40px',
              backgroundColor: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderRadius: '4px',
            }}
          />
        ) : (
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={localValue}
            onChange={handleTextFieldChange}
            onBlur={handleBlur}
            placeholder={placeholder}
            disabled={disabled}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              sx: { fontFamily: 'monospace', fontSize: '14px' },
            }}
          />
        )}
      </Paper>

      {/* Preview Section */}
      {showPreview && localValue && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility fontSize="small" />
            Preview:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mt: 1,
              backgroundColor: 'grey.50',
              minHeight: 40,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <LaTeXPreview latex={localValue} displayMode />
          </Paper>
        </Box>
      )}

      {/* Validation Messages */}
      {showValidation && localValue.trim().length > 0 && (
        <Box sx={{ mt: 2 }}>
          {isValidating && (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Validating expression...
              </Box>
            </Alert>
          )}

          {!isValidating && validation && validation.errors.length > 0 && (
            <Alert
              severity="error"
              icon={<Warning />}
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" component="div">
                <strong>Errors:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {validation.errors.map((error, index) => (
                    <li key={index}>{getFriendlyErrorMessage(error)}</li>
                  ))}
                </ul>
              </Typography>
            </Alert>
          )}

          {!isValidating && validation && validation.warnings.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" component="div">
                <strong>Warnings:</strong>
                <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                  {validation.warnings.map((warning, index) => (
                    <li key={index}>{warning}</li>
                  ))}
                </ul>
              </Typography>
            </Alert>
          )}

          {!isValidating && validation && validation.isValid && validation.errors.length === 0 && validation.warnings.length === 0 && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{ mb: 1 }}
            >
              Expression is valid
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MathInputField;